<template>
  <div class="static-proxy-page">
    <!-- Header -->
    <a-layout-header class="header">
      <div class="header-content">
        <div class="logo-container" @click="$router.push('/')">
          <span class="logo-icon">S</span>
          <div class="logo-text">xxxxproxy</div>
        </div>
        <a-menu
          theme="light"
          mode="horizontal"
          :default-selected-keys="['static']"
          class="nav-menu"
        >
          <a-menu-item key="1"><router-link to="/">{{ $t('menu.home') }}</router-link></a-menu-item>
          <a-menu-item key="2"><router-link to="/dynamic-residential-proxy">{{ $t('menu.dynamicProxy') }}</router-link></a-menu-item>
          <a-menu-item key="static"><router-link to="/static-residential-proxy">{{ $t('menu.staticProxy') }}</router-link></a-menu-item>
          <a-menu-item key="3"><router-link to="/help-center">{{ $t('menu.helpCenter') }}</router-link></a-menu-item>
        </a-menu>
        <div class="auth-buttons">
          <template v-if="!isLoggedIn">
            <a-button type="link" class="login-btn" @click="$router.push('/login')">{{ $t('menu.login') }}</a-button>
            <a-button type="primary" class="register-btn" @click="$router.push('/register')">{{ $t('menu.register') }}</a-button>
          </template>
          <template v-else>
            <div class="user-profile-wrapper">
              <a-dropdown :trigger="['hover']" placement="bottomRight" :getPopupContainer="triggerNode => triggerNode.parentNode">
                <div class="user-profile-dropdown" @click="$router.push('/dashboard/auth')">
                  <a-avatar icon="user" class="avatar" />
                  <span class="username">{{ username }}</span>
                  <a-icon type="down" />
                </div>
                <a-menu slot="overlay" class="user-dropdown-menu">
                  <a-menu-item key="dashboard" @click="$router.push('/dashboard/auth')">
                    <a-icon type="dashboard" /> {{ $t('menu.dashboard') }}
                  </a-menu-item>
                  <a-menu-divider />
                  <a-menu-item key="logout" @click="handleLogout" class="logout-item">
                    <a-icon type="logout" /> {{ $t('menu.logout') }}
                  </a-menu-item>
                </a-menu>
              </a-dropdown>
            </div>
          </template>
          <a-icon type="menu" class="mobile-menu-btn" @click="showMobileMenu = true" />
        </div>
      </div>
    </a-layout-header>

    <!-- Mobile Menu Drawer -->
    <a-drawer
      placement="right"
      :visible="showMobileMenu"
      @close="showMobileMenu = false"
      :closable="false"
      width="280"
      wrapClassName="mobile-menu-drawer"
    >
      <div class="drawer-content">
        <div class="drawer-header">
          <div class="drawer-brand">
            <span class="logo-icon">S</span>
            <span class="logo-text">xxxxproxy</span>
          </div>
          <div class="close-btn" @click="showMobileMenu = false">
            <a-icon type="close" />
          </div>
        </div>
        
        <div class="drawer-menu">
          <router-link to="/" class="menu-link" exact-active-class="active">
            <a-icon type="home" /> {{ $t('menu.home') }}
          </router-link>
          <router-link to="/dynamic-residential-proxy" class="menu-link" active-class="active">
            <a-icon type="thunderbolt" /> {{ $t('menu.dynamicProxy') }}
          </router-link>
          <router-link to="/static-residential-proxy" class="menu-link" active-class="active">
            <a-icon type="cloud-server" /> {{ $t('menu.staticProxy') }}
          </router-link>
          <router-link to="/help-center" class="menu-link" active-class="active">
            <a-icon type="question-circle" /> {{ $t('menu.helpCenter') }}
          </router-link>
        </div>

        <div class="drawer-auth">
          <template v-if="!isLoggedIn">
            <a-button block size="large" class="drawer-login-btn" @click="$router.push('/login')">{{ $t('menu.login') }}</a-button>
            <a-button block type="primary" size="large" class="drawer-register-btn" @click="$router.push('/register')">{{ $t('register.registerBtn') }}</a-button>
          </template>
          <template v-else>
            <div class="drawer-user-info">
              <a-avatar size="large" icon="user" class="avatar" />
              <div class="user-details">
                <span class="username">{{ username }}</span>
                <span class="status">在线</span>
              </div>
            </div>
            <a-button block size="large" class="drawer-dashboard-btn" @click="$router.push('/dashboard/auth')">
              <a-icon type="dashboard" /> {{ $t('menu.dashboard') }}
            </a-button>
            <a-button block size="large" type="danger" ghost class="drawer-logout-btn" @click="handleLogout">
              <a-icon type="logout" /> {{ $t('menu.logout') }}
            </a-button>
          </template>
        </div>
      </div>
    </a-drawer>

    <!-- Product Hero -->
    <div class="product-hero">
      <div class="hero-bg-animated">
        <div class="glow-orb glow-1"></div>
        <div class="glow-orb glow-2"></div>
      </div>
      <div class="hero-content">
        <h1>{{ $t('staticProxy.hero.title') }}</h1>
        <p class="hero-subtitle" v-html="$t('staticProxy.hero.subtitle')"></p>
        <div class="hero-actions">
          <a-button type="primary" size="large" class="cta-btn" @click="$router.push('/login')">{{ $t('dynamicProxy.hero.buyNow') }}</a-button>
          <a-button size="large" ghost class="contact-btn" @click="handleContactClick">{{ $t('dynamicProxy.hero.contactSales') }}</a-button>
        </div>
      </div>
    </div>

    <!-- Key Features -->
    <div class="features-section">
      <h2 class="section-title">{{ $t('staticProxy.features.title') }}</h2>
      <a-row :gutter="[24, 24]">
        <a-col :md="8" :sm="24" :xs="24" v-for="(feature, index) in features" :key="index">
          <div class="feature-item">
            <a-icon :type="feature.icon" class="feature-icon" />
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.description }}</p>
          </div>
        </a-col>
      </a-row>
    </div>

    <!-- Use Cases -->
    <div class="use-cases-section">
      <h2 class="section-title">{{ $t('staticProxy.useCases.title') }}</h2>
      <a-row :gutter="24">
        <a-col :md="8" :sm="24" :xs="24" v-for="(caseItem, index) in useCases" :key="index" style="margin-bottom: 24px;">
          <a-card hoverable class="use-case-card">
            <div class="case-icon-wrapper">
              <a-icon :type="caseItem.icon" />
            </div>
            <h3>{{ caseItem.title }}</h3>
            <p>{{ caseItem.description }}</p>
          </a-card>
        </a-col>
      </a-row>
    </div>

    <!-- Advantages Section -->
    <div class="advantages-section">
      <div class="advantages-container">
        <h2 class="section-title">{{ $t('staticProxy.advantages.title') }}</h2>
        <div class="advantages-grid">
          <div class="advantage-item" v-for="(adv, index) in advantages" :key="index">
            <div class="adv-number">0{{ index + 1 }}</div>
            <div class="adv-content">
              <h3>{{ adv.title }}</h3>
              <p>{{ adv.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <a-layout-footer class="footer">
      <div class="footer-content">
        <a-row :gutter="48">
          <a-col :md="8" :sm="24" :xs="24">
            <div class="footer-logo">xxxxproxy</div>
            <p>{{ $t('home.footer.desc') }}</p>
          </a-col>
          <a-col :md="16" :sm="24" :xs="24">
            <a-row :gutter="24">
              <a-col :span="8">
                <h4>{{ $t('home.footer.products') }}</h4>
                <p><router-link to="/dynamic-residential-proxy">{{ $t('menu.dynamicProxy') }}</router-link></p>
                <p><router-link to="/static-residential-proxy">{{ $t('menu.staticProxy') }}</router-link></p>
              </a-col>
              <a-col :span="8">
                <h4>{{ $t('home.footer.support') }}</h4>
                <p><router-link to="/help-center">{{ $t('menu.helpCenter') }}</router-link></p>
              </a-col>
              <a-col :span="8">
                <h4>{{ $t('home.footer.contact') }}</h4>
                <p>Email: zongtaiyida@gmail.com</p>
              </a-col>
            </a-row>
          </a-col>
        </a-row>
        <div class="copyright">
          {{ $t('home.footer.copyright', { year: new Date().getFullYear() }) }}
        </div>
      </div>
    </a-layout-footer>

    <!-- Back Top -->
    <a-back-top :visibilityHeight="300">
      <div class="back-to-top-btn">
        <a-icon type="arrow-up" />
      </div>
    </a-back-top>
  </div>
</template>

<script>
export default {
  name: 'StaticResidentialProxy',
  data() {
    return {
      isLoggedIn: false,
      username: '',
      showMobileMenu: false
    };
  },
  computed: {
    features() {
      return [
        {
          title: this.$t('staticProxy.features.speed'),
          description: this.$t('staticProxy.features.speedDesc'),
          icon: 'rocket'
        },
        {
          title: this.$t('staticProxy.features.stability'),
          description: this.$t('staticProxy.features.stabilityDesc'),
          icon: 'safety-certificate'
        },
        {
          title: this.$t('staticProxy.features.location'),
          description: this.$t('staticProxy.features.locationDesc'),
          icon: 'environment'
        },
        {
          title: this.$t('staticProxy.features.ip'),
          description: this.$t('staticProxy.features.ipDesc'),
          icon: 'pushpin'
        },
        {
          title: this.$t('staticProxy.features.pure'),
          description: this.$t('staticProxy.features.pureDesc'),
          icon: 'safety'
        },
        {
          title: this.$t('staticProxy.features.support'),
          description: this.$t('staticProxy.features.supportDesc'),
          icon: 'customer-service'
        }
      ];
    },
    useCases() {
      return [
        {
          title: this.$t('staticProxy.useCases.socialMedia.title'),
          description: this.$t('staticProxy.useCases.socialMedia.desc'),
          icon: 'team'
        },
        {
          title: this.$t('staticProxy.useCases.eCommerce.title'),
          description: this.$t('staticProxy.useCases.eCommerce.desc'),
          icon: 'shopping-cart'
        },
        {
          title: this.$t('staticProxy.useCases.marketResearch.title'),
          description: this.$t('staticProxy.useCases.marketResearch.desc'),
          icon: 'line-chart'
        },
        {
          title: this.$t('staticProxy.useCases.seo.title'),
          description: this.$t('staticProxy.useCases.seo.desc'),
          icon: 'search'
        },
        {
          title: this.$t('staticProxy.useCases.adVerification.title'),
          description: this.$t('staticProxy.useCases.adVerification.desc'),
          icon: 'safety-certificate'
        },
        {
          title: this.$t('staticProxy.useCases.brandProtection.title'),
          description: this.$t('staticProxy.useCases.brandProtection.desc'),
          icon: 'safety'
        }
      ];
    },
    advantages() {
      return [
        {
          title: this.$t('staticProxy.advantages.items[0].title'),
          desc: this.$t('staticProxy.advantages.items[0].desc')
        },
        {
          title: this.$t('staticProxy.advantages.items[1].title'),
          desc: this.$t('staticProxy.advantages.items[1].desc')
        },
        {
          title: this.$t('staticProxy.advantages.items[2].title'),
          desc: this.$t('staticProxy.advantages.items[2].desc')
        }
      ];
    }
  },
  mounted() {
    this.checkLoginStatus();
    this.initScrollReveal();
  },
  methods: {
    handleContactClick() {
      window.location.href = 'mailto:zongtaiyida@gmail.com';
    },
    checkLoginStatus() {
      const token = localStorage.getItem('Token');
      const username = localStorage.getItem('userName');
      if (token && username) {
        this.isLoggedIn = true;
        this.username = username;
      }
    },
    handleLogout() {
      localStorage.clear();
      this.isLoggedIn = false;
      this.username = '';
      this.$message.success('退出登录成功');
      this.$router.push('/login');
    },
    initScrollReveal() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });

      setTimeout(() => {
        const animatedElements = document.querySelectorAll('.features-section, .use-cases-section, .advantages-section');
        animatedElements.forEach(el => {
          el.classList.add('scroll-reveal');
          observer.observe(el);
        });
      }, 100);
    }
  }
};
</script>

<style lang="less">
.user-dropdown-menu {
  padding: 8px;
  border-radius: 12px;
  box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #f0f7ff;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  min-width: 160px;
  
  .ant-dropdown-menu-item {
    border-radius: 8px;
    padding: 10px 16px;
    margin-bottom: 4px;
    font-weight: 500;
    color: #475569;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    
    .anticon {
      margin-right: 12px;
      font-size: 16px;
      color: #94a3b8;
      transition: all 0.2s;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
    
    &:hover {
      background: #eff6ff;
      color: #1890ff;
      transform: translateX(2px);
      
      .anticon {
        color: #1890ff;
      }
    }
  }

  .ant-dropdown-menu-item-divider {
    background-color: #f1f5f9;
    margin: 6px 0;
  }
  
  .logout-item {
    &:hover {
      background: #fff1f2;
      color: #ef4444;
      
      .anticon {
        color: #ef4444;
      }
    }
  }
}
</style>

<style scoped lang="less">
// 变量定义
@primary-color: #1890ff;
@secondary-color: #69c0ff;
@dark-bg: #0A192F;
@text-main: #1e293b;
@text-light: #64748b;

.static-proxy-page {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8fafc;
}

/* Animations */
@keyframes float {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
  100% { transform: translateY(0px); }
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(61, 90, 254, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(61, 90, 254, 0); }
  100% { box-shadow: 0 0 0 0 rgba(61, 90, 254, 0); }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translate3d(0, 40px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

.scroll-reveal {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s ease-out;
}

.scroll-reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}

/* Header Styles */
.header {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  box-shadow: 0 1px 0 rgba(0,0,0,0.05);
  padding: 0;
  height: 72px;
  line-height: 72px;
  position: sticky;
  top: 0;
  z-index: 1000;
}
.header-content {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
}
.logo-container {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}
.logo-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, @primary-color, @secondary-color);
  color: #fff;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 20px;
}
.logo-text {
  font-size: 22px;
  font-weight: 800;
  color: @text-main;
  letter-spacing: -0.5px;
}
.nav-menu {
  flex: 1;
  border-bottom: none;
  line-height: 70px;
  background: transparent;
  display: flex;
  justify-content: center;
}
.nav-menu .ant-menu-item {
  font-size: 15px;
  font-weight: 500;
  margin: 0 16px;
  top: 0;
}
.nav-menu .ant-menu-item a {
  color: @text-main;
}
.nav-menu .ant-menu-item a:hover {
  color: @primary-color;
}
.nav-menu .ant-menu-item-selected {
  border-bottom: 2px solid @primary-color;
}
.nav-menu .ant-menu-item-selected a {
  color: @primary-color;
}
.auth-buttons {
  display: flex;
  align-items: center;
  gap: 12px;
}
.auth-buttons .login-btn {
  color: @text-main;
  font-weight: 600;
  font-size: 15px;
}
.auth-buttons .login-btn:hover {
  color: @primary-color;
}
.auth-buttons .register-btn {
  height: 40px;
  padding: 0 24px;
  font-size: 15px;
  border-radius: 20px;
  font-weight: 600;
  background: @text-main;
  border-color: @text-main;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}
.auth-buttons .register-btn:hover {
  background: @primary-color;
  border-color: @primary-color;
}

.auth-buttons .user-profile-dropdown {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 8px 4px 4px;
  height: 40px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  line-height: 1.5;
  
  &:hover {
    background: rgba(0, 0, 0, 0.04);
    
    .username {
      color: @primary-color;
    }
    
    .anticon-down {
      color: @primary-color;
      transform: rotate(180deg);
    }
  }
  
  .avatar {
    width: 30px;
    height: 30px;
    line-height: 30px;
    font-size: 16px;
    background: linear-gradient(135deg, @primary-color, @secondary-color);
    border: 1px solid #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.3s;
  }
  
  .username {
    font-weight: 600;
    color: @text-main;
    font-size: 14px;
    max-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: color 0.3s;
  }
  
  .anticon-down {
    font-size: 12px;
    color: #94a3b8;
    margin-right: 4px;
    transition: all 0.3s;
  }
}

.mobile-menu-btn {
  display: none;
  font-size: 24px;
  color: #333;
  margin-left: 15px;
  cursor: pointer;
}

/* Hero Section */
.product-hero {
  position: relative;
  background: @dark-bg;
  color: #fff;
  padding: 120px 0 100px;
  text-align: center;
  overflow: hidden;
}
.hero-bg-animated {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
.glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.3;
  animation: float 10s infinite ease-in-out;
}
.glow-1 {
  width: 400px;
  height: 400px;
  background: @primary-color;
  top: -100px;
  left: -100px;
}
.glow-2 {
  width: 500px;
  height: 500px;
  background: @secondary-color;
  bottom: -100px;
  right: -100px;
  animation-delay: -2s;
}

.hero-content {
  position: relative;
  z-index: 10;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 24px;
}
.hero-content h1 {
  font-size: 56px;
  font-weight: 800;
  color: #fff;
  margin-bottom: 24px;
  animation: fadeInUp 0.8s ease-out;
  line-height: 1.2;
}
.hero-subtitle {
  font-size: 20px;
  color: #94a3b8;
  margin-bottom: 48px;
  line-height: 1.6;
  animation: fadeInUp 0.8s ease-out 0.2s backwards;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}
.hero-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
}
.hero-actions .cta-btn {
  height: 56px;
  padding: 0 40px;
  font-size: 18px;
  border-radius: 28px;
  background: linear-gradient(135deg, @primary-color, @secondary-color);
  border: none;
  animation: fadeInUp 0.8s ease-out 0.4s backwards;
}
.hero-actions .cta-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(61, 90, 254, 0.4);
}
.hero-actions .contact-btn {
  height: 56px;
  padding: 0 40px;
  font-size: 18px;
  border-radius: 28px;
  border: 1px solid rgba(255,255,255,0.2);
  color: #fff;
  animation: fadeInUp 0.8s ease-out 0.5s backwards;
}
.hero-actions .contact-btn:hover {
  background: rgba(255,255,255,0.1);
  border-color: #fff;
}

/* Features Section */
.features-section {
  padding: 100px 24px;
  max-width: 1280px;
  margin: 0 auto;
}
.section-title {
  text-align: center;
  font-size: 36px;
  font-weight: 800;
  margin-bottom: 60px;
  color: @text-main;
}
.feature-item {
  text-align: center;
  padding: 40px 30px;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  transition: all 0.3s;
  height: 100%;
  border: 1px solid #f1f5f9;
}
.feature-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0,0,0,0.08);
  border-color: @primary-color;
}
.feature-item:hover .feature-icon {
  animation: float 2s ease-in-out infinite;
  transform: scale(1.1);
  color: @primary-color;
}
.feature-icon {
  font-size: 48px;
  color: @text-main;
  margin-bottom: 24px;
  transition: all 0.3s;
}
.feature-item h3 {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 12px;
  color: @text-main;
}
.feature-item p {
  color: @text-light;
  line-height: 1.6;
  font-size: 15px;
}

/* Advantages Section */
.advantages-section {
  padding: 80px 48px;
  background: #f8fafc;
  
  .advantages-container {
    max-width: 1200px;
    margin: 0 auto;
  }
  
  .section-title {
    text-align: center;
    font-size: 36px;
    font-weight: 800;
    color: @text-main;
    margin-bottom: 60px;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -16px;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 4px;
      background: linear-gradient(90deg, @primary-color, @secondary-color);
      border-radius: 2px;
    }
  }

  .advantages-grid {
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  .advantage-item {
    display: flex;
    background: #ffffff;
    border-radius: 24px;
    padding: 40px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
    align-items: flex-start;
    
    &:hover {
      transform: translateX(10px);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
      border-color: rgba(24, 144, 255, 0.2);
      
      .adv-number {
        color: @primary-color;
        transform: scale(1.1);
      }
    }
    
    .adv-number {
      font-size: 64px;
      font-weight: 900;
      color: #e2e8f0;
      line-height: 1;
      margin-right: 40px;
      transition: all 0.3s ease;
      font-family: 'Inter', sans-serif;
    }
    
    .adv-content {
      flex: 1;
      
      h3 {
        font-size: 24px;
        font-weight: 700;
        color: @text-main;
        margin-bottom: 16px;
      }
      
      p {
        font-size: 16px;
        color: @text-light;
        line-height: 1.8;
        margin: 0;
      }
    }
  }
}
.use-cases-section {
  padding: 80px 48px;
  background: #ffffff;
  
  .section-title {
    text-align: center;
    font-size: 36px;
    font-weight: 800;
    color: @text-main;
    margin-bottom: 60px;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -16px;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 4px;
      background: linear-gradient(90deg, @primary-color, @secondary-color);
      border-radius: 2px;
    }
  }

  .use-case-card {
    height: 100%;
    border-radius: 16px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    background: #ffffff;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:hover {
      transform: translateY(-8px);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
      border-color: rgba(24, 144, 255, 0.2);
      
      .case-icon-wrapper {
        background: linear-gradient(135deg, @primary-color, @secondary-color);
        color: #ffffff;
        transform: scale(1.1);
      }
    }
    
    .ant-card-body {
      padding: 32px 24px;
      text-align: center;
    }
    
    .case-icon-wrapper {
      width: 64px;
      height: 64px;
      margin: 0 auto 24px;
      border-radius: 16px;
      background: #f0f7ff;
      color: @primary-color;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 28px;
      transition: all 0.3s ease;
    }
    
    h3 {
      font-size: 20px;
      font-weight: 700;
      color: @text-main;
      margin-bottom: 16px;
    }
    
    p {
      color: @text-light;
      font-size: 15px;
      line-height: 1.6;
      margin: 0;
    }
  }
}

/* Footer */
.footer {
  background: #fff;
  padding: 80px 0 40px;
  border-top: 1px solid #f1f5f9;
  margin-top: auto;
}
.footer-content {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
}
.footer-logo {
  font-size: 24px;
  font-weight: 800;
  color: @text-main;
  margin-bottom: 16px;
  letter-spacing: -0.5px;
}
.footer h4 {
  color: @text-main;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 24px;
}
.footer p {
  margin-bottom: 12px;
  color: @text-light;
}
.footer a {
  color: @text-light;
  transition: color 0.3s;
}
.footer a:hover {
  color: @primary-color;
}
.copyright {
  border-top: 1px solid #f1f5f9;
  margin-top: 60px;
  padding-top: 30px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
}

/* Back Top */
  .back-to-top-btn {
    width: 48px;
    height: 48px;
    background: @primary-color;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 24px;
    box-shadow: 0 8px 20px rgba(61, 90, 254, 0.4);
    transition: all 0.3s;
    
    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 12px 24px rgba(61, 90, 254, 0.5);
      background: lighten(@primary-color, 5%);
    }
  }

  @media (max-width: 768px) {
  /* Header */
  .header {
    height: 60px;
    line-height: 60px;
    padding: 0 10px;
  }
  .header-content {
    padding: 0 10px;
  }
  .logo-container {
    margin-right: 10px;
  }
  .logo-text {
    font-size: 18px;
  }
  .logo-icon {
    width: 28px;
    height: 28px;
    font-size: 16px;
  }
  .nav-menu {
    display: none; 
  }
  .auth-buttons .login-btn,
  .auth-buttons .register-btn {
    display: none;
  }
  .user-profile-wrapper {
    padding-bottom: 10px;
    margin-bottom: -10px;
  }
  
  .user-profile-dropdown {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 8px 4px 4px;
    height: 40px;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    line-height: 1.5;
    
    &:hover {
      background: rgba(0, 0, 0, 0.04);
      
      .username {
        color: @primary-color;
      }
      
      .anticon-down {
        color: @primary-color;
        transform: rotate(180deg);
      }
    }
    
    .avatar {
      width: 30px;
      height: 30px;
      line-height: 30px;
      font-size: 16px;
      background: linear-gradient(135deg, @primary-color, @secondary-color);
      border: 1px solid #fff;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      transition: all 0.3s;
    }
    
    .username {
      font-weight: 600;
      color: @text-main;
      font-size: 14px;
      max-width: 100px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      transition: color 0.3s;
    }
    
    .anticon-down {
      font-size: 12px;
      color: #94a3b8;
      margin-right: 4px;
      transition: all 0.3s;
    }
  }

  .mobile-menu-btn {
    display: block;
  }

  /* Mobile Menu Styles */
  /deep/ .mobile-menu-drawer .ant-drawer-body {
    padding: 0;
    height: 100%;
  }

  .drawer-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 24px;
    background: #fff;
  }

  .drawer-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 40px;
    
    .drawer-brand {
      display: flex;
      align-items: center;
      gap: 12px;
      
      .logo-icon {
        width: 32px;
        height: 32px;
        background: linear-gradient(135deg, @primary-color, @secondary-color);
        border-radius: 8px;
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 800;
        font-size: 18px;
      }
      
      .logo-text {
        font-size: 20px;
        font-weight: 800;
        color: @text-main;
      }
    }
    
    .close-btn {
      font-size: 20px;
      color: @text-light;
      cursor: pointer;
      padding: 8px;
      border-radius: 50%;
      background: #f1f5f9;
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s;
      
      &:active {
        background: #e2e8f0;
        transform: scale(0.95);
      }
    }
  }

  .drawer-menu {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
    
    .menu-link {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 16px;
      border-radius: 12px;
      color: @text-main;
      font-size: 16px;
      font-weight: 600;
      transition: all 0.3s;
      background: #f8fafc;
      
      i {
        font-size: 18px;
        color: @text-light;
        transition: color 0.3s;
      }
      
      &.active, &:active {
        background: #eff6ff;
        color: @primary-color;
        
        i {
          color: @primary-color;
        }
      }
    }
  }

  .drawer-auth {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #f1f5f9;
    
    .drawer-user-info {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 12px;
      background: #f8fafc;
      border-radius: 12px;
      margin-bottom: 8px;
      
      .avatar {
        background: linear-gradient(135deg, @primary-color, @secondary-color);
        box-shadow: 0 4px 12px rgba(24, 144, 255, 0.2);
      }
      
      .user-details {
        display: flex;
        flex-direction: column;
        
        .username {
          font-weight: 700;
          color: @text-main;
          font-size: 16px;
        }
        
        .status {
          font-size: 12px;
          color: #10b981;
          display: flex;
          align-items: center;
          gap: 4px;
          
          &::before {
            content: '';
            width: 6px;
            height: 6px;
            background: #10b981;
            border-radius: 50%;
            display: inline-block;
          }
        }
      }
    }
    
    .drawer-login-btn, .drawer-dashboard-btn {
      border-radius: 12px;
      height: 48px;
      font-weight: 600;
      font-size: 16px;
      border: 1px solid #e2e8f0;
      color: @text-main;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      
      &:active {
        background: #f1f5f9;
      }
    }
    
    .drawer-logout-btn {
      border-radius: 12px;
      height: 48px;
      font-weight: 600;
      font-size: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
    
    .drawer-register-btn {
      border-radius: 12px;
      height: 48px;
      font-weight: 600;
      font-size: 16px;
      background: @text-main;
      border-color: @text-main;
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      
      &:active {
        background: @primary-color;
        border-color: @primary-color;
        transform: scale(0.98);
      }
    }
  }

  .product-hero {
    padding: 100px 0 60px;
  }
  .hero-content h1 {
    font-size: 32px;
  }
  .hero-subtitle {
    font-size: 16px;
    padding: 0 20px;
  }
  .hero-actions {
    flex-direction: column;
    padding: 0 24px;
    align-items: center;
  }
  .hero-actions .cta-btn, .hero-actions .contact-btn {
    width: 100%;
    margin: 0 0 16px 0;
  }

  .features-section, .use-cases-section, .advantages-section {
    padding: 60px 24px;
  }
  .advantage-item {
    flex-direction: column;
    padding: 24px;
    
    .adv-number {
      font-size: 48px;
      margin-right: 0;
      margin-bottom: 20px;
    }
  }
  .section-title {
    font-size: 28px;
    margin-bottom: 40px;
  }

  
  /* Footer */
  .footer {
    padding: 40px 0 20px;
    text-align: center;
  }
  .footer .ant-col-8,
  .footer .ant-col-16 {
    width: 100% !important;
    flex: 0 0 100% !important;
    max-width: 100% !important;
    display: block;
    text-align: center;
    margin-bottom: 30px;
  }
  .footer .ant-col-span-8 {
    width: 100% !important;
    flex: 0 0 100% !important;
    max-width: 100% !important;
    display: block;
    margin-bottom: 20px;
  }
}
</style>
